import {Component, ElementRef, EventEmitter, OnInit, Output, ViewChild} from "@angular/core";
import {
    ITCIFile,
    ITCIFileMap,
    TCIApplicationCreateOption,
    TCITenantAppType,
    TCITenantSelectorType
} from "@tci/common";
import {
    ITCICreateAppComponentInput,
    ITCIUploadComponentDroppedFilesOutput,
    ITCIUploadComponentInput,
    ITCIUploadFileCheckResult,
    ITCIUploadSupportedFile,
    TCITenantComponentInput
} from "../../../ng-tenant/tci-tenant-components";
import {AbstractTCITenantComponent} from "../../../ng-tenant/tci-tenant-component";

import {UploadEvent} from "ngx-file-drop";
import {Observable} from "rxjs";
import * as lodash from "lodash";

export class BaseCreateAppImportV2ComponentInput extends TCITenantComponentInput implements ITCICreateAppComponentInput {
    constructor(private type: TCITenantAppType, protected option: TCIApplicationCreateOption) {
        super();
    }

    /**
     * Get the component tenant type
     */
    getTenantType(): TCITenantAppType {
        return this.type;
    }

    /**
     * Get the component create option
     */
    getCreateOption(): TCIApplicationCreateOption {
        return this.option;
    }

    /**
     * Get the component tenant type label
     */
    getTenantTypeLabel(): String {
        return TCITenantAppType.node.equals(this.type.toString()) ? TCITenantAppType.node.toString() : this.type.toString();
    }
}

const ENCODING_STR = "Binary";

@Component({
    selector: TCITenantSelectorType.TCI_APP_CREATE_FILE_UPLOAD.toString(),
    templateUrl: "./base-create-app-import.component.html"
})
export abstract class BaseCreateAppImportV2Component<E, I extends ITCIUploadComponentInput,
    O extends ITCIUploadComponentDroppedFilesOutput | boolean> extends AbstractTCITenantComponent<E, I, O> implements OnInit {
    @Output() output: any = new EventEmitter();
    @ViewChild("inputFiles") FilesInput: ElementRef;

    private allowedFiles: ITCIUploadSupportedFile[] = [];
    private files: ITCIFileMap = {};
    private fileNames = {};
    private droppedFiles: { [key: string]: ITCIUploadFileCheckResult } = {};
    private errorMsg: string;

    static ReadFile(fileEntry: File, isBinary: boolean = true, encoding: string = ENCODING_STR): Observable<ITCIFile> {
        return Observable.create(observer => {
            const reader = new FileReader();
            reader.onloadend = f => {
                const res: ITCIFile = {
                    content: <string>reader.result,
                    mimeType: fileEntry.type,
                    encoding: encoding
                };
                observer.next(res);
                observer.complete();
            };
            reader.onerror = f => {
                const msg = "File is not readable";
                console.error(msg, f);
                observer.throw(new Error(msg));
                observer.complete();
            };
            if (!isBinary && encoding !== ENCODING_STR) {
                reader.readAsText(fileEntry, encoding);
            } else {
                reader.readAsBinaryString(fileEntry);
            }
        });
    }

    public abstract getFileSpecs(): ITCIUploadSupportedFile[];

    constructor() {
        super();
    }

    ngOnInit() {
        this.initFilesInput();
    }

    private initFilesInput() {
        this.allowedFiles = this.getFileSpecs();

        if (this.FilesInput && this.FilesInput.nativeElement) {
            this.FilesInput.nativeElement
                .addEventListener("change", (e: any) => this.handleSelectedFiles(<File[]>lodash.toArray(e.target.files)));
        }
    }

    private setAllowedFiles(f: ITCIUploadSupportedFile[]) { // To be called from extended class component
        this.allowedFiles = f;
        this.resetFiles();
    }

    private resetFiles() {
        this.allowedFiles.forEach(f => this.removeFile(f));
    }

    private dropped(event: UploadEvent) {
        const files: File[] = [];
        let processed = 0;
        event.files
            .filter(f => f.fileEntry.isFile)
            .forEach(f => {
                f.fileEntry.file((fe: File) => {
                    files.push(fe);

                    processed++;
                    if (processed === event.files.length) {
                        this.handleSelectedFiles(files);
                    }
                });
            });
    }

    private handleSelectedFiles(files: File[]) {
        if (files.length === 0) { // IPAS-12209
            return;
        }

        this.errorMsgHide();
        const emitErr = (msg: string) => {
            this.errorMsg = msg;
            if (lodash.size(this.droppedFiles) < this.allowedFiles.length) { // There'are already valid files
                this.output.emit(false);
            }
        };

        if (this.allowedFiles.length < files.length) {
            emitErr("There are more selected files than expected");
        } else if (
            this.allowedFiles.length > files.length &&
            !this.allowedFiles.some(a => files.map(f => f.name.toLowerCase()).some(f => f.endsWith("." + a.extension)))
        ) {
            emitErr("There are unsupported files in your selection");
        } else if (
            this.allowedFiles.length === files.length &&
            !this.allowedFiles.every(a => files.map(f => f.name.toLowerCase()).some(f => f.endsWith("." + a.extension)))
        ) {
            emitErr("There are unsupported files in your selection");
        } else if (lodash.some(lodash.countBy(files, file => lodash.last(file.name.split("."))), count => count > 1)) {
            emitErr(`Selected files have more than one ${this.allowedFiles.map(f => f.extension).join(" or ")}`);
        } else {
            const fileCheck: Observable<ITCIUploadFileCheckResult>[] = files.map(file => Observable.create(observer => {
                const name: string = file.name;
                const fileSpec: ITCIUploadSupportedFile = lodash.first(this.allowedFiles.filter(f => name.toLowerCase().endsWith("." + f.extension)));
                const isBinary = lodash.isUndefined(fileSpec.isBinary) ? true : fileSpec.isBinary;
                const encoding = lodash.isUndefined(fileSpec.encoding) ? ENCODING_STR : fileSpec.encoding;
                const res: ITCIUploadFileCheckResult = {
                    type: fileSpec.extension,
                    fileContent: null, name, error: false, errorMsg: null
                };

                if (!this.files[res.type]) { this.files[res.type] = <any>{}; }
                this.files[res.type].content = "";

                BaseCreateAppImportV2Component.ReadFile(file, isBinary, encoding)
                    .finally(() => {
                        observer.next(res);
                        observer.complete();
                    })
                    .subscribe(
                        fileContent => res.fileContent = fileContent,
                        () => {
                            res.error = true;
                            res.errorMsg = "Can't read one or more selected files";
                        }
                    );
            }));

            Observable.merge(...fileCheck)
                .toArray()
                .subscribe(DroppedFiles => {
                    if (!DroppedFiles.some(fcr => fcr.error)) {
                        DroppedFiles.forEach(fr => {
                            this.files[fr.type] = fr.fileContent;
                            this.fileNames[fr.type] = fr.name;
                            this.droppedFiles[fr.type] = fr; // Feed the dropped files array
                        });
                        this.output.emit({ DroppedFiles: lodash.toArray(this.droppedFiles) });
                    } else {
                        this.errorMsg = lodash.sample(DroppedFiles).errorMsg;
                        this.output.emit(false);
                    }
                });
        }
    }

    private removeFile(file: ITCIUploadSupportedFile) {
        if (this.files[file.extension] && this.files[file.extension].content && !lodash.isEmpty(this.files[file.extension].content)) {
            this.errorMsgHide();
            this.FilesInput.nativeElement.value = "";
            const encoding = lodash.isUndefined(file.encoding) ? ENCODING_STR : file.encoding;
            this.files[file.extension] = { content: null, encoding: encoding, mimeType: null };
            this.fileNames[file.extension] = "";
            delete this.droppedFiles[file.extension];

            if (file.optional !== true) { // Disable push if not optional
                this.output.emit(false);
            }
        }
    }

    private isFileValid(file: ITCIUploadSupportedFile) {
        return this.files[file.extension]
            && !lodash.isNull(this.files[file.extension].content)
            && !lodash.isEmpty(this.files[file.extension].content)
            && !lodash.isEmpty(this.files[file.extension].encoding);
    }

    public errorMsgHide() {
        this.errorMsg = undefined;
    }

    public setErrorMsg(msg: string) {
        this.errorMsg = msg;
    }

    isObjectEmpty(obj): boolean {
        return Object.keys(obj).length === 0;
    }
}
